import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Languages, RefreshCw, AlertTriangle, CheckCircle2, XCircle,
  ArrowLeft, FileWarning, Globe2, Trash2, Bug,
} from 'lucide-react';
import { getMissingKeys, clearMissingKeys } from '../i18n/runtimeGuard';

const GRADE_COLOR = {
  A: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  B: 'text-lime-700 bg-lime-50 border-lime-200',
  C: 'text-amber-700 bg-amber-50 border-amber-200',
  D: 'text-orange-700 bg-orange-50 border-orange-200',
  F: 'text-rose-700 bg-rose-50 border-rose-200',
};

const SEV_CHIP = {
  CRITICAL: 'bg-rose-100 text-rose-800 border-rose-200',
  WARNING: 'bg-amber-100 text-amber-800 border-amber-200',
};

const Stat = ({ icon: Icon, label, value, tone = 'indigo', testid }) => (
  <div
    data-testid={testid}
    className={`rounded-xl border border-${tone}-100 bg-white p-4 flex items-center gap-3`}
  >
    <div className={`p-2 rounded-lg bg-${tone}-50`}>
      <Icon className={`w-5 h-5 text-${tone}-700`} />
    </div>
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-bold text-gray-900">{value}</div>
    </div>
  </div>
);

const Section = ({ title, subtitle, actions, children, testid }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={testid}>
    <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
      <div>
        <h3 className="font-bold text-gray-900 text-sm">{title}</h3>
        {subtitle && <p className="text-[11px] text-gray-500">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const I18nHealth = () => {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [issues, setIssues] = useState([]);
  const [runtimeMissing, setRuntimeMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('CRITICAL');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [healthRes, issuesRes] = await Promise.all([
          axiosInstance.get('/i18n-health/run'),
          axiosInstance.get('/i18n-health/issues', { params: { severity: filter, limit: 100 } }),
        ]);
        if (cancelled) return;
        setHealth(healthRes.data);
        setIssues(issuesRes.data.items || []);
        setRuntimeMissing(getMissingKeys());
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || 'Failed to load i18n health';
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter, reloadKey]);

  const fetchAll = useCallback(() => setReloadKey((k) => k + 1), []);

  const gradeClass = useMemo(() => GRADE_COLOR[health?.grade] || GRADE_COLOR.F, [health]);

  const byModule = useMemo(() => {
    if (!health?.by_module) return [];
    return Object.entries(health.by_module)
      .map(([mod, v]) => ({ mod, ...v, total: (v.critical || 0) + (v.warning || 0) }))
      .sort((a, b) => b.critical - a.critical || b.warning - a.warning)
      .slice(0, 15);
  }, [health]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="i18n-health-page">
      <button
        onClick={() => navigate('/settings')}
        className="flex items-center gap-2 text-sm text-indigo-700 hover:underline"
        data-testid="back-to-settings"
      >
        <ArrowLeft className="w-4 h-4" /> Settings
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Languages className="w-6 h-6 text-indigo-700" />
            i18n Health Dashboard
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            P11.7 — Enforcement Layer. Coverage, hardcoded strings, runtime missing keys.
          </p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          data-testid="refresh-i18n-health"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Rescan
        </button>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div
          data-testid="i18n-grade-card"
          className={`rounded-xl border p-4 flex flex-col gap-1 ${gradeClass}`}
        >
          <div className="text-xs uppercase opacity-70">Coverage</div>
          <div className="text-3xl font-bold leading-none">
            {health?.coverage_pct ?? '—'}%
          </div>
          <div className="text-xs">Grade {health?.grade || '—'}</div>
        </div>
        <Stat icon={CheckCircle2} label="Pages OK" tone="emerald"
              value={`${(health?.total_pages || 0) - (health?.risky_pages || 0)}/${health?.total_pages || 0}`}
              testid="i18n-pages-ok" />
        <Stat icon={XCircle} label="Critical" tone="rose"
              value={health?.critical ?? '—'} testid="i18n-critical-count" />
        <Stat icon={AlertTriangle} label="Warnings" tone="amber"
              value={health?.warning ?? '—'} testid="i18n-warning-count" />
        <Stat icon={Globe2} label="Translation keys" tone="indigo"
              value={`${health?.translation_keys?.en_total ?? 0}/${health?.translation_keys?.ar_total ?? 0}`}
              testid="i18n-keys-count" />
      </div>

      {/* Translation parity */}
      {health?.translation_keys?.missing_total > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="i18n-parity-warning">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
            <FileWarning className="w-4 h-4" /> Translation parity: {health.translation_keys.missing_total} key(s) differ between EN and AR
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-3 text-xs">
            <div>
              <div className="font-semibold text-amber-900">Missing in AR ({health.translation_keys.missing_in_ar.length})</div>
              <ul className="mt-1 space-y-0.5 max-h-40 overflow-auto">
                {health.translation_keys.missing_in_ar.map((k) => (
                  <li key={`ar-${k}`} className="font-mono text-amber-900/80">{k}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-semibold text-amber-900">Missing in EN ({health.translation_keys.missing_in_en.length})</div>
              <ul className="mt-1 space-y-0.5 max-h-40 overflow-auto">
                {health.translation_keys.missing_in_en.map((k) => (
                  <li key={`en-${k}`} className="font-mono text-amber-900/80">{k}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Per-module breakdown */}
      <Section title="Risk by module" subtitle="Highest-critical first" testid="i18n-by-module">
        {byModule.length === 0 ? (
          <div className="text-xs text-gray-500">No data.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Module</th>
                  <th className="py-2">Critical</th>
                  <th className="py-2">Warnings</th>
                  <th className="py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {byModule.map((m) => (
                  <tr key={m.mod} className="border-b last:border-0">
                    <td className="py-1.5 font-mono">{m.mod}</td>
                    <td className="py-1.5 text-rose-700 font-semibold">{m.critical || 0}</td>
                    <td className="py-1.5 text-amber-700">{m.warning || 0}</td>
                    <td className="py-1.5 text-gray-700">{m.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Issue list */}
      <Section
        title="Static scan violations"
        subtitle="From scripts/i18n_scan.py — same engine as CI"
        testid="i18n-issues"
        actions={
          <div className="flex items-center gap-2 text-xs">
            {['CRITICAL', 'WARNING'].map((sev) => (
              <button
                key={sev}
                onClick={() => setFilter(sev)}
                data-testid={`filter-${sev.toLowerCase()}`}
                className={`px-2.5 py-1 rounded border ${
                  filter === sev ? SEV_CHIP[sev] : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                {sev}
              </button>
            ))}
          </div>
        }
      >
        {issues.length === 0 ? (
          <div className="text-xs text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> No {filter.toLowerCase()} violations.
          </div>
        ) : (
          <div className="max-h-96 overflow-auto divide-y">
            {issues.map((i, idx) => (
              <div key={`${i.file}:${i.line}:${idx}`} className="py-2 text-xs flex items-start gap-3">
                <span className={`px-2 py-0.5 rounded border text-[10px] uppercase ${SEV_CHIP[i.severity]}`}>
                  {i.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-gray-700 truncate">{i.file}:{i.line} <span className="text-gray-400">[{i.kind}]</span></div>
                  <div className="text-gray-900 break-words">{i.text}</div>
                  {i.suggestion && <div className="text-[11px] text-indigo-700 mt-0.5">→ {i.suggestion}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Runtime guard */}
      <Section
        title="Runtime Guard — missing keys observed in this browser"
        subtitle="Captured by frontend/src/i18n/runtimeGuard.js"
        testid="i18n-runtime"
        actions={
          <button
            onClick={() => { clearMissingKeys(); setRuntimeMissing([]); toast.success('Cleared'); }}
            data-testid="clear-runtime-missing"
            className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-gray-200 text-xs text-gray-700 hover:bg-gray-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear
          </button>
        }
      >
        {runtimeMissing.length === 0 ? (
          <div className="text-xs text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> No missing keys detected in this session.
          </div>
        ) : (
          <div className="max-h-72 overflow-auto divide-y">
            {runtimeMissing.slice(-50).reverse().map((m, idx) => (
              <div key={`${m.key}-${idx}`} className="py-1.5 text-xs flex items-center gap-3">
                <Bug className="w-3.5 h-3.5 text-rose-600" />
                <span className="font-mono text-gray-900">{m.key}</span>
                <span className="text-gray-400">lng={m.lng}</span>
                <span className="text-gray-400 truncate">{m.page}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

export default I18nHealth;
